/**
 * API Client for Lambda Backend
 *
 * This client handles communication with the Lambda function API.
 * - Loaders: GET requests for page-specific data (single round trip)
 * - Actions: POST requests for business operations (mutations)
 */

import { ENVS, getAuthWrapper } from "~/utils";
import { getVitestParallelPoolId } from "~/utils/vitestParallelPool";
import { loadCookiesData } from "~/utils/auth/loadCookiesData";
import * as APITypes from "~/API";
import type { FormattedResourceAccessRight } from "~/types";
import type {
  ExpiringLeasesResult,
  OutsideRentMonthLeasesResult,
  RentCollectionResult,
  StayCollectionResult,
} from "~/types/finance";

// ============================================================================
// Types
// ============================================================================

export type OccupancyBlockerKind = "lease" | "stay" | "block";

export type OccupancyBlocker = {
  kind: OccupancyBlockerKind;
  id: string;
  label: string;
  start: string;
  end: string;
};

export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error?: string;
  /** Lambda `errorWithDebugLogs` / `forbiddenWithDebugLogs` traces (non-production logging). */
  debugLogs?: string[];
  /** Occupancy conflict code from stay/lease intents. */
  code?: string;
  blockers?: OccupancyBlocker[];
}

/** Return value for route actions when `sendIntent` fails; forwards `debugLogs` for dev tooling. */
export type IntentActionFailure = {
  success: false;
  error: string;
  debugLogs?: string[];
  code?: string;
  blockers?: OccupancyBlocker[];
};

export function intentFailureFromApiResponse<T>(
  result: ApiResponse<T>,
  fallbackMessage: string,
): IntentActionFailure {
  console.error("[Intent]", fallbackMessage, result.error ?? "");
  if (Array.isArray(result.debugLogs) && result.debugLogs.length > 0) {
    console.error(
      "[Intent debugLogs]\n",
      result.debugLogs.map((line) => `  ${line}`).join("\n"),
    );
  }
  return {
    success: false,
    error: fallbackMessage,
    ...(Array.isArray(result.debugLogs) && result.debugLogs.length > 0
      ? { debugLogs: result.debugLogs }
      : {}),
    ...(typeof result.code === "string" ? { code: result.code } : {}),
    ...(Array.isArray(result.blockers) ? { blockers: result.blockers } : {}),
  };
}

export interface OwnerContext {
  ownerID: string;
  ownerType: APITypes.OwnerType;
}

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = ENVS.lambda_api_url;

/**
 * Appended after the Firebase JWT in Authorization when parallel staging tests run.
 * Must match `TEST_WORKER_TOKEN_MARKER` in propertyMgmtApiFunction `firebaseAuth.ts`.
 * `|` does not appear in JWT base64url segments.
 */
const PARALLEL_TEST_TOKEN_MARKER = "|||TW|||";

function appendParallelTestTokenSuffix(token: string): string {
  const enabled =
    process.env.REACT_APP_USE_PARALLEL_TEST_TOKEN_SUFFIX === "true" ||
    process.env.REACT_APP_USE_PARALLEL_TEST_TOKEN_SUFFIX === "1";
  if (!enabled) {
    return token;
  }
  const poolId = getVitestParallelPoolId();
  if (!poolId) {
    return token;
  }
  return `${token}${PARALLEL_TEST_TOKEN_MARKER}${poolId}`;
}

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

let cachedIdToken: { token: string; expiresAt: number } | null = null;

export function clearCachedIdToken(): void {
  cachedIdToken = null;
}

async function getCachedIdToken(forceRefresh = false): Promise<string | undefined> {
  const user = getAuthWrapper().currentUser;
  if (!user) {
    clearCachedIdToken();
    return undefined;
  }

  if (
    !forceRefresh &&
    cachedIdToken &&
    Date.now() < cachedIdToken.expiresAt - TOKEN_REFRESH_BUFFER_MS
  ) {
    return cachedIdToken.token;
  }

  try {
    const token = await user.getIdToken(forceRefresh);
    cachedIdToken = {
      token,
      expiresAt: Date.now() + 55 * 60 * 1000,
    };
    return token;
  } catch (err) {
    clearCachedIdToken();
    console.warn("Could not get auth token:", err);
    return undefined;
  }
}

// ============================================================================
// Mock registry (only used when NODE_ENV === 'test')
// ============================================================================

const isTestEnv =
  (typeof process !== "undefined" && process.env?.NODE_ENV === "test") ||
  (typeof import.meta !== "undefined" && !!(import.meta as { vitest?: boolean }).vitest);

type MockResponse = ApiResponse<unknown> | (() => ApiResponse<unknown>);
const mockRegistry = new Map<string, MockResponse>();

function getMockKey(endpoint: string, method: string, body?: unknown): string | null {
  if (method === "GET" && endpoint.startsWith("/loader/")) {
    return "loader:" + endpoint.slice("/loader/".length);
  }
  if (method === "POST" && endpoint === "/action" && body && typeof body === "object") {
    const b = body as Record<string, unknown>;
    if (typeof b.action === "string") return "action:" + b.action;
    if (typeof b.intent === "string") return "intent:" + b.intent;
  }
  return null;
}

// ============================================================================
// API Client
// ============================================================================

export const apiClient = {
  /**
   * Loader - Fetch page-specific data in a single request
   *
   * @example
   * // Fetch property details page data
   * const result = await apiClient.loader<PropertyDetailsLoaderResult>(
   *   'property-details/abc123'
   * );
   *
   * @example
   * // Fetch property table with owner filter
   * const result = await apiClient.loader<PropertyTableLoaderResult>(
   *   'property-table',
   *   { ownerID: '123', ownerType: 'User' }
   * );
   */
  async loader<T>(
    loaderPath: string,
    params?: Record<string, string | undefined>
  ): Promise<ApiResponse<T>> {
    // Build query string from params (filtering out undefined values)
    const filteredParams = params
      ? Object.fromEntries(
          Object.entries(params).filter(([_, v]) => v !== undefined)
        )
      : {};

    const queryString =
      Object.keys(filteredParams).length > 0
        ? "?" + new URLSearchParams(filteredParams as Record<string, string>).toString()
        : "";

    return this.request<T>(`/loader/${loaderPath}${queryString}`, "GET");
  },

  /**
   * Action - Execute a business operation
   *
   * Actions are mutations that may involve multiple database operations.
   * The Lambda function handles all business logic, validation, and cascading.
   *
   * @example
   * // Create a property
   * const result = await apiClient.action<Property>('property.create', {
   *   name: 'My Property',
   *   city: 'Austin',
   *   state: 'TX',
   *   country: 'USA',
   *   contact: 'test@example.com',
   * });
   *
   * @example
   * // Delete a property (cascades to buildings, units, etc.)
   * const result = await apiClient.action('property.delete', {
   *   propertyId: 'abc123',
   *   _version: 5,
   * });
   */
  async action<T>(
    actionName: string,
    payload: Record<string, unknown>
  ): Promise<ApiResponse<T>> {
    // Get authentication info
    const auth = getAuthWrapper();
    const user = auth.currentUser;

    if (!user) {
      return { success: false, data: null, error: "Not authenticated" };
    }

    // Get owner context from cookies
    let ownerContext: OwnerContext | undefined;
    try {
      const authAccessInfo = await loadCookiesData(user.uid);
      if (authAccessInfo.success && authAccessInfo.data) {
        ownerContext = {
          ownerID: authAccessInfo.data.ownerID,
          ownerType: authAccessInfo.data.ownerType,
        };
      }
    } catch (err) {
      console.warn("Could not load owner context:", err);
    }

    // Build action body
    const body = {
      action: actionName,
      ownerContext,
      userName: user.displayName || user.email || undefined,
      ...payload,
    };

    return this.request<T>("/action", "POST", body);
  },

  /**
   * Send intent - Execute an intent (e.g. _edit_building) via Lambda.
   * Body sent as { intent, ownerContext, userName, ...payload }.
   */
  async sendIntent<T>(
    intentName: string,
    payload: Record<string, unknown>
  ): Promise<ApiResponse<T>> {
    const auth = getAuthWrapper();
    const user = auth.currentUser;

    if (!user) {
      return { success: false, data: null, error: "Not authenticated" };
    }

    let ownerContext: OwnerContext | undefined;
    try {
      const authAccessInfo = await loadCookiesData(user.uid);
      if (authAccessInfo.success && authAccessInfo.data) {
        ownerContext = {
          ownerID: authAccessInfo.data.ownerID,
          ownerType: authAccessInfo.data.ownerType,
        };
      }
    } catch (err) {
      console.warn("Could not load owner context:", err);
    }

    const body = {
      intent: intentName,
      ownerContext,
      userName: user.displayName || user.email || undefined,
      ...payload,
    };

    return this.request<T>("/action", "POST", body);
  },

  /**
   * Internal request handler
   */
  async request<T>(
    endpoint: string,
    method: string,
    body?: unknown,
    retryOnUnauthorized = true,
  ): Promise<ApiResponse<T>> {
    if (isTestEnv) {
      const key = getMockKey(endpoint, method, body);
      if (key) {
        const mock = mockRegistry.get(key);
        if (mock !== undefined) {
          const result = typeof mock === "function" ? mock() : mock;
          return result as ApiResponse<T>;
        }
      }
    }

    const token = await getCachedIdToken();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      const tokenWithOptionalSuffix = appendParallelTestTokenSuffix(token);
      headers["Authorization"] = `Bearer ${tokenWithOptionalSuffix}`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json();

      if (response.status === 401 && retryOnUnauthorized) {
        const refreshedToken = await getCachedIdToken(true);
        if (refreshedToken && refreshedToken !== token) {
          return this.request<T>(endpoint, method, body, false);
        }
      }

      if (!response.ok) {
        const errData = data as Record<string, unknown>;
        const debugLogs = Array.isArray(errData.debugLogs)
          ? (errData.debugLogs as string[])
          : undefined;
        if (debugLogs?.length && process.env.NODE_ENV !== "production") {
          console.error(
            `[API ${response.status}] ${(errData.error as string) || "Error"}\n  Debug logs:\n${debugLogs.map((l) => `    ${l}`).join("\n")}`,
          );
        }
        return {
          success: false,
          data: null,
          error: (errData.error as string) || `HTTP ${response.status}`,
          ...(debugLogs && { debugLogs }),
          ...(typeof errData.code === "string" ? { code: errData.code } : {}),
          ...(Array.isArray(errData.blockers)
            ? { blockers: errData.blockers as OccupancyBlocker[] }
            : {}),
        };
      }

      return data as ApiResponse<T>;
    } catch (error) {
      console.error("API request failed:", error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  },

  // --------------------------------------------------------------------------
  // Mock API (for tests; mock lookup only runs when NODE_ENV === 'test')
  // Keys: 'loader:<path>', 'action:<name>', 'intent:<name>'
  // --------------------------------------------------------------------------
  setMock(key: string, result: ApiResponse<unknown> | (() => ApiResponse<unknown>)): void {
    mockRegistry.set(key, result);
  },
  setMockFailure(key: string, errorMessage: string): void {
    mockRegistry.set(key, { success: false, data: null, error: errorMessage });
  },
  clearMock(key: string): void {
    mockRegistry.delete(key);
  },
  clearMocks(): void {
    mockRegistry.clear();
  },
};

// ============================================================================
// Loader Result Types (match Lambda loaders/property.ts)
// ============================================================================

// S3 file info with pre-signed URL from Lambda
export interface S3FileWithUrl {
  Key: string;
  LastModified?: string;
  Size?: number;
  ETag?: string;
  url: string;
}

// Files structure from Lambda
export interface LambdaFilesResult {
  files: S3FileWithUrl[];
  images: S3FileWithUrl[];
}

export interface PropertyDetailsLoaderResult {
  propertyDetails: APITypes.Property;
  buildingList: APITypes.Building[];
  parcelList: APITypes.Parcel[];
  amenityList: APITypes.Amenity[];
  unitPlanList: UnitPlanTableItem[];
  serviceRequestList: APITypes.ServiceRequest[];
  tenantList: APITypes.Tenant[];
  files: LambdaFilesResult;
  permissions: {
    canReadProperty: boolean;
    canEditProperty: boolean;
    canDeleteProperty: boolean;
    canCreateServiceRequest: boolean;
    isOwner: boolean;
  };
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
}

export interface PropertyTableLoaderResult {
  properties: APITypes.Property[];
  totalCount: number;
}

export interface BuildingTableLoaderResult {
  buildingList: Array<{
    buildingDetails: APITypes.Building;
    propertyDetails: APITypes.Property;
    unitList: APITypes.Unit[];
  }>;
  totalCount: number;
}

export interface ParcelTableLoaderResult {
  parcelList: Array<{
    parcelDetails: APITypes.Parcel;
    propertyDetails: APITypes.Property;
  }>;
  totalCount: number;
}

export interface BuildingDetailsLoaderResult {
  buildingDetails: APITypes.Building;
  propertyDetails: APITypes.Property;
  unitList: Array<{
    unitDetails: APITypes.Unit;
    buildingDetails: APITypes.Building;
    propertyDetails: APITypes.Property;
    accessRights?: APITypes.ResourceUpdateAccessRight | null;
  }>;
  formattedServiceRequestList: Array<
    APITypes.ServiceRequest & {
      entityDetails: {
        property?: { success: boolean; data: APITypes.Property };
        building?: { success: boolean; data: APITypes.Building };
        unit?: { success: boolean; data: APITypes.Unit };
        parcel?: { success: boolean; data: APITypes.Parcel };
      };
      accessRights?: APITypes.ResourceUpdateAccessRight | null;
    }
  >;
  tenantList: APITypes.Tenant[];
  permissions: {
    canReadBuilding: boolean;
    canEditBuilding: boolean;
    canDeleteBuilding: boolean;
    canCreateServiceRequest: boolean;
  };
  /** Always set by Lambda (real or default plan). No Amplify subscription call needed in loader. */
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
  files: LambdaFilesResult;
}

export interface AddParcelPageLoaderResult {
  propertyList: APITypes.Property[];
  initialProperty: APITypes.Property;
  canCreateParcel: boolean;
}

export interface ParcelDetailsLoaderResult {
  parcelDetails: APITypes.Parcel;
  propertyDetails: APITypes.Property;
  leaseList: APITypes.Lease[];
  stays: APITypes.Stay[];
  stayRate: APITypes.StayRate | null;
  stayBlocks: APITypes.StayAvailability[];
  occupancyLeases: APITypes.Lease[];
  formattedServiceRequestList: Array<
    APITypes.ServiceRequest & {
      entityDetails: {
        property?: { success: boolean; data: APITypes.Property };
        parcel?: { success: boolean; data: APITypes.Parcel };
      };
      accessRights?: APITypes.ResourceUpdateAccessRight | null;
    }
  >;
  tenantList: APITypes.Tenant[];
  permissions: {
    canReadParcel: boolean;
    canEditParcel: boolean;
    canDeleteParcel: boolean;
    canCreateServiceRequest: boolean;
    canCreateLease: boolean;
    canCreateStay: boolean;
  };
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
  files: LambdaFilesResult;
}

export interface UnitTableLoaderResult {
  unitList: Array<{
    unitDetails: APITypes.Unit;
    buildingDetails: APITypes.Building;
    propertyDetails: APITypes.Property;
  }>;
  propertyList: APITypes.Property[];
  totalCount: number;
}

export interface UnitDisplayAttributes {
  description: string | null;
  numBedroom: string;
  numBathroom: string;
  size: string | null;
  inUnitLaundry: boolean | null;
  dishwasher: boolean | null;
  airConditioning: boolean | null;
  furnished: boolean | null;
  balcony: boolean | null;
  petFriendly: boolean | null;
}

export interface UnitDetailsLoaderResult {
  unitDetails: APITypes.Unit;
  buildingDetails: APITypes.Building;
  propertyDetails: APITypes.Property;
  formattedServiceRequestList: Array<
    APITypes.ServiceRequest & {
      entityDetails: {
        property?: { success: boolean; data: APITypes.Property };
        building?: { success: boolean; data: APITypes.Building };
        unit?: { success: boolean; data: APITypes.Unit };
        parcel?: { success: boolean; data: APITypes.Parcel };
      };
      accessRights?: APITypes.ResourceUpdateAccessRight | null;
    }
  >;
  tenantList: APITypes.Tenant[];
  permissions: {
    canReadUnit: boolean;
    canEditUnit: boolean;
    canDeleteUnit: boolean;
    canCreateServiceRequest: boolean;
    canReadUnitPlan: boolean;
    canCreateStay: boolean;
    canCreateTenant: boolean;
  };
  linkedUnitPlan: APITypes.UnitPlan | null;
  displayAttributes: UnitDisplayAttributes;
  /** Unit plans for this unit's property (for link/unlink in edit drawer). */
  propertyUnitPlanList: UnitPlanTableItem[];
  /** Always set by Lambda (real or default plan). No Amplify subscription call needed in loader. */
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
  hasFinanceAccess: boolean;
  files: LambdaFilesResult;
  stays: APITypes.Stay[];
  stayRate: APITypes.StayRate | null;
  stayBlocks: APITypes.StayAvailability[];
  occupancyLeases: APITypes.Lease[];
}

export interface UnitPlanTableItem {
  planDetails: APITypes.UnitPlan;
  linkedUnitCount: number;
  permissions: {
    canReadUnitPlan: boolean;
    canEditUnitPlan: boolean;
    canDeleteUnitPlan: boolean;
  };
}

export interface UnitPlanTableLoaderResult {
  unitPlanList: UnitPlanTableItem[];
  propertyDetails?: APITypes.Property;
  totalCount: number;
}

export interface UnitPlanDetailsLoaderResult {
  planDetails: APITypes.UnitPlan;
  propertyDetails: APITypes.Property;
  linkedUnits: APITypes.Unit[];
  unitsAvailableToLink: APITypes.Unit[];
  buildingMap: Record<string, APITypes.Building>;
  permissions: {
    canReadUnitPlan: boolean;
    canEditUnitPlan: boolean;
    canDeleteUnitPlan: boolean;
    isAdminOrSuperAdmin: boolean;
  };
  files: LambdaFilesResult;
}

export interface AddUnitPlanPageLoaderResult {
  property: APITypes.Property;
  canCreateUnitPlan: boolean;
}

export interface UnitDetailsLeaseLoaderResult {
  unitDetails: APITypes.Unit;
  formattedLeaseList: Array<{
    leaseInfo: APITypes.Lease;
    tenantInfo: APITypes.Tenant;
    entityType: APITypes.EntityType;
    entityDetails: APITypes.Unit | APITypes.Building | APITypes.Parcel;
    advanceLeaseAction: string;
    cancelLeaseAction: string;
  }>;
  tenantList: APITypes.Tenant[];
  unitListMap: {
    propertyList: APITypes.Property[];
    buildingList: APITypes.Building[];
    unitList: APITypes.Unit[];
    serviceRequestList: APITypes.ServiceRequest[];
    amenityList: APITypes.Amenity[];
    propertyMap: Record<string, APITypes.Property>;
    buildingMap: Record<string, APITypes.Building>;
    unitMap: Record<string, APITypes.Unit>;
  };
}

export interface ServiceRequestTableLoaderResult {
  serviceRequestList: Array<
    APITypes.ServiceRequest & {
      entityDetails: {
        property?: { success: boolean; data: APITypes.Property };
        building?: { success: boolean; data: APITypes.Building };
        unit?: { success: boolean; data: APITypes.Unit };
        parcel?: { success: boolean; data: APITypes.Parcel };
      };
      accessRights?: APITypes.ResourceUpdateAccessRight | null;
    }
  >;
  tenantList: APITypes.Tenant[];
  totalCount: number;
  serviceRequestLifecycleAccess?: {
    createAccessStatus: APITypes.ResourceAccessRightStatus;
  } | null;
  subscriptionPlan?: APITypes.SubscriptionPlan | null;
  hasServiceRequestAccess?: boolean;
}

export interface ServiceRequestDetailsLoaderResult {
  serviceRequest: APITypes.ServiceRequest;
  comments: APITypes.Comments[];
  entityDetails: {
    property?: { success: boolean; data: APITypes.Property };
    building?: { success: boolean; data: APITypes.Building };
    unit?: { success: boolean; data: APITypes.Unit };
    parcel?: { success: boolean; data: APITypes.Parcel };
  };
  entityType: APITypes.EntityType;
  permissions: {
    canReadRequest: boolean;
    canEditRequest: boolean;
    canDeleteRequest: boolean;
  };
  /** Always set by Lambda (real or default plan). No Amplify subscription call needed in loader. */
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
  files: LambdaFilesResult;
}

export interface TransactionTableLoaderResult {
  transactionList: Array<{
    id: string;
    details: APITypes.Transaction;
    entityDetails: {
      unit?: { success: boolean; data: APITypes.Unit };
      building?: { success: boolean; data: APITypes.Building };
      parcel?: { success: boolean; data: APITypes.Parcel };
    };
    tenantInfo: APITypes.Tenant;
    accessRights?: APITypes.ResourceUpdateAccessRight | null;
  }>;
  tenantList: APITypes.Tenant[];
  unitList: Array<{
    unitDetails: APITypes.Unit;
    buildingDetails: APITypes.Building;
    propertyDetails: APITypes.Property;
    accessRights?: APITypes.ResourceUpdateAccessRight | null;
  }>;
  propertyList: APITypes.Property[];
  buildingList: APITypes.Building[];
  totalCount: number;
  hasMore: boolean;
  nextToken?: string | null;
  /** When userID provided: lifecycle access for Transaction entity. */
  transactionLifecycleAccess?: {
    createAccessStatus: APITypes.ResourceAccessRightStatus;
  } | null;
}

export interface TenantTableLoaderResult {
  tenantList: APITypes.Tenant[];
  totalCount: number;
}

export interface TenantDetailsLeaseLoaderResult {
  tenantInfo: APITypes.Tenant;
  formattedLeaseList: Array<{
    leaseInfo: APITypes.Lease;
    tenantInfo: APITypes.Tenant;
    entityType: APITypes.EntityType;
    entityDetails: APITypes.Unit | APITypes.Building | APITypes.Parcel;
    advanceLeaseAction: string;
    cancelLeaseAction: string;
  }>;
}

export interface TenantDetailsLoaderResult {
  tenantDetails: APITypes.Tenant;
  formattedServiceRequestList: Array<
    APITypes.ServiceRequest & {
      entityDetails: {
        property?: { success: boolean; data: APITypes.Property };
        building?: { success: boolean; data: APITypes.Building };
        unit?: { success: boolean; data: APITypes.Unit };
        parcel?: { success: boolean; data: APITypes.Parcel };
      };
      accessRights?: APITypes.ResourceUpdateAccessRight | null;
    }
  >;
  tenantList: APITypes.Tenant[];
  permissions: {
    canCreateServiceRequest: boolean;
  };
  stays: APITypes.Stay[];
}

export interface LeaseTableLoaderResult {
  leaseList: Array<{
    leaseInfo: APITypes.Lease;
    tenantInfo: APITypes.Tenant;
    entityType: APITypes.EntityType;
    entityDetails: APITypes.Unit | APITypes.Building | APITypes.Parcel;
  }>;
  tenantList: APITypes.Tenant[];
  unitList: Array<{
    unitDetails: APITypes.Unit;
    buildingDetails: APITypes.Building;
    propertyDetails: APITypes.Property;
  }>;
  propertyList: APITypes.Property[];
  totalCount: number;
}

export interface LeaseDetailsLoaderResult {
  formattedLease: {
    leaseInfo: APITypes.Lease;
    tenantInfo: APITypes.Tenant;
    entityType: APITypes.EntityType;
    entityDetails: APITypes.Unit | APITypes.Building | APITypes.Parcel;
    application?: APITypes.Application;
    background?: APITypes.BackgroundCheck;
    offer?: APITypes.Offer;
    moveIn?: APITypes.MoveExam;
    moveOut?: APITypes.MoveExam;
    advanceLeaseAction: string;
    cancelLeaseAction: string;
  };
  unitDetails: APITypes.Unit;
  nextLease: LeaseDetailsLoaderResult["formattedLease"] | null;
}

export interface LeaseUpdateTableLoaderResult {
  leaseList: LeaseDetailsLoaderResult["formattedLease"][];
}

export interface LeaseUpdateDetailsLoaderResult {
  formattedLease: LeaseDetailsLoaderResult["formattedLease"];
  unitDetails: APITypes.Unit;
  comment: string;
}

export interface AmenityDetailsLoaderResult {
  amenityDetails: APITypes.Amenity;
  propertyDetails: APITypes.Property;
  buildingDetails: APITypes.Building;
  reservations: Array<{
    startDate: string;
    endDate: string;
    tenantName: string;
    createdAt: string;
    data: APITypes.AmenityReservation;
  }>;
  accessRights?: APITypes.ResourceUpdateAccessRight | null;
  buildingList: APITypes.Building[];
  permissions: {
    canDeleteAmenity: boolean;
    canEditAmenity: boolean;
    canReadAmenity: boolean;
    isAdminOrSuperAdmin: boolean;
  };
  files: { files: Array<{ Key: string; url?: string }>; images: Array<{ Key: string; url?: string }> };
}

export interface AmenityTableLoaderResult {
  amenityList: Array<{
    amenityDetails: APITypes.Amenity;
    propertyDetails: APITypes.Property;
    buildingDetails?: APITypes.Building | null;
    reservations: Array<{
      reservation: APITypes.AmenityReservation;
      tenantName?: string;
    }>;
    accessRights?: APITypes.ResourceUpdateAccessRight | null;
  }>;
  propertyList: APITypes.Property[];
  totalCount: number;
}

export interface AddPropertyLoaderResult {
  canCreateProperty: boolean;
}

export interface FinancePageLoaderResult {
  financeStatusSummary: APITypes.YearlyStatusSummary;
  financeStatusMonthlyList: APITypes.MonthlyStatusReport[];
  propertyFinanceStatusMonthlyList: Array<{
    propertyInfo: APITypes.Property;
    report: APITypes.YearlyStatusSummary;
  }>;
  canCreateReport: boolean;
  canDeleteReport: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasFinanceAccess: boolean;
  rentCollection: RentCollectionResult;
  stayCollection: StayCollectionResult;
  expiringLeases: ExpiringLeasesResult;
  outsideRentMonthLeases: OutsideRentMonthLeasesResult;
}

export interface PropertyFinancePageLoaderResult {
  propertyDetails: APITypes.Property;
  financeStatusSummary: APITypes.YearlyStatusSummary;
  financeStatusMonthlyList: APITypes.MonthlyStatusReport[];
  canCreateReport: boolean;
  canDeleteReport: boolean;
  canReadProperty: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasFinanceAccess: boolean;
  rentCollection: RentCollectionResult;
  stayCollection: StayCollectionResult;
  expiringLeases: ExpiringLeasesResult;
  outsideRentMonthLeases: OutsideRentMonthLeasesResult;
}

export interface LandingPageLoaderResult {
  plans: Array<{
    title: string;
    price: string;
    description: string;
    features: string[];
    value: string;
    buttonText: string;
    highlight?: boolean;
    highlightLabel?: string;
  }>;
}

export interface HomePageOperationsLoaderResult {
  unitCount: number;
  offMarketCount: number;
  occupiedCount: number;
  vacancyCount: number;
  inRenovationCount: number;
  inProgressServiceRequests: number;
  expiringLeases: ExpiringLeasesResult;
  rentNeverPaidCount: number;
  rentInProgressCount: number;
  rentNeverPaidBalanceDue: number;
  rentInProgressBalanceDue: number;
  rentCollectionMonth: number;
  rentCollectionYear: number;
  leasesAwaitingReviewCount: number;
  pendingAmenityReservationsCount: number;
  pendingTransactionsCount: number;
  hasShortTermStayInventory?: boolean;
  pendingStaysCount?: number;
  arrivalsTodayCount?: number;
  departuresTodayCount?: number;
  noShowCount?: number;
}

export interface HomePageAnalyticsLoaderResult {
  monthlyIncome: number;
  yearlyIncome: number;
  monthlyExpenses: number;
  yearlyExpenses: number;
  monthlyNet: number;
  yearlyNet: number;
}

/** @deprecated Use split operations + analytics loaders. */
export interface HomePageLoaderResult extends HomePageOperationsLoaderResult {
  completedServiceRequests: number;
  monthlyIncome: number;
  yearlyIncome: number;
  monthlyExpenses: number;
  yearlyExpenses: number;
  monthlyNet: number;
  yearlyNet: number;
}

export interface OrganizationRedirectLoaderResult {
  organizationID: string | null;
  roles: APITypes.ManagerRolesType | null;
}

export interface OrganizationDetailsLoaderResult {
  organizationDetails: APITypes.Organization;
  membersList: APITypes.User[];
  joinRequests: Array<{
    request: APITypes.JoinOrganizationRequest;
    user: APITypes.User | null;
  }>;
  userName: string;
}

export interface OrgMemberDetailsLoaderResult {
  memberDetails: APITypes.User;
  organizationDetails: APITypes.Organization;
  userName: string;
}

export interface LifecycleAccessTabLoaderResult {
  accessRights: APITypes.ResourceLifeAccessRight[];
  memberID: string;
  memberRole: APITypes.ManagerRolesType | null;
}

export interface InstanceAccessTabLoaderResult {
  accessRights: FormattedResourceAccessRight[];
  memberID: string;
  memberRole: APITypes.ManagerRolesType | null;
}

export interface AddAmenityPageLoaderResult {
  property: APITypes.Property;
  buildingList: APITypes.Building[];
  canCreateAmenity: boolean;
}

export interface AddPropertyPageLoaderResult {
  canCreateProperty: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  currentPropertyCount: number;
  maxProperties: number;
}

export interface AddBuildingPageLoaderResult {
  propertyList: APITypes.Property[];
  initialProperty: APITypes.Property;
  canCreateBuilding: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  currentBuildingCount: number;
  maxBuildings: number;
}

export interface AddUnitPageLoaderResult {
  buildingList: APITypes.Building[];
  propertyMap: Record<string, APITypes.Property>;
  initialBuilding: APITypes.Building;
  canCreateUnit: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  currentUnitCount: number;
  maxUnits: number;
}

export interface AddTenantPageLoaderResult {
  canCreateTenant: boolean;
}

export interface StayTableLoaderResult {
  stays: Array<{
    stay: APITypes.Stay;
    tenantName: string;
    inventoryLabel: string;
    inventoryHref: string;
  }>;
}

// ============================================================================
// Export default
// ============================================================================

export default apiClient;
